import React, { useState, useEffect, useRef } from "react";
import { Placeholder } from "react-bootstrap";
import BoxShadow from "../../../components/layout/BoxShadow.jsx";
import Style from "../../../styles/features/vita/ScoreVita.module.css";
import Fire from "../../../components/ui/Fire/Fire.jsx";
import { isStreakBroken, todayKeySP } from "../../../utils/streakStatus.js";
import { FaFireAlt } from "react-icons/fa";
import { PiStarFourFill } from "react-icons/pi";
import useCountUp from "../../../hooks/useCountUp.js";
import useTabCountUp from "../../../hooks/useTabCountUp.js";
import { PiSnowflakeFill } from "react-icons/pi";


// ── Tooltip genérico ─────────────────────────────────────────
export const InfoTooltip = ({ text }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    const t = setTimeout(() => setOpen(false), 5000); // auto-fecha em 5s
    return () => { document.removeEventListener("mousedown", handler); document.removeEventListener("touchstart", handler); clearTimeout(t); };
  }, [open]);

  return (
    <span className={Style.infoWrap} ref={ref}>
      <button
        type="button"
        className={Style.infoBtn}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        aria-label="Informação"
      >
        i
      </button>
      {open && <span className={Style.infoTooltip}>{text}</span>}
    </span>
  );
};



// ── Arco SVG — círculo completo, sentido horário, começa às 12h ──
export const ArcProgress = ({ percent = 0, size = 104, stroke = 8, isActive = true }) => {
  const [displayPercent, setDisplayPercent] = useState(percent);
  const lastPercentRef = useRef(percent);

  useEffect(() => {
    if (!isActive) {
      setDisplayPercent(lastPercentRef.current); // mantém stale enquanto inativo
      return;
    }
    // Ativa: transiciona para o valor atual via CSS transition (já existe no stroke)
    const t = setTimeout(() => {
      setDisplayPercent(percent);
      lastPercentRef.current = percent;
    }, 60);
    return () => clearTimeout(t);
  }, [percent, isActive]);

  const r    = (size - stroke * 1) / 2;
  const cx   = size / 2;
  const circ = 2 * Math.PI * r;
  const arc  = Math.min(100, displayPercent) / 100 * circ;
  
  return (
    <svg
      width={size}
      height={size}
      style={{ transform: "rotate(-90deg)" }}   /* 12h = topo */
    >
      {/* trilha */}
      <circle
        cx={cx} cy={cx} r={r}
        fill="none"
        stroke="color-mix(in srgb, var(--color-accent) 12%, transparent)"
        strokeWidth={stroke}
      />
      {/* progresso — clockwise por padrão no SVG */}
      <circle
        cx={cx} cy={cx} r={r}
        fill="none"
        stroke="var(--color-accent)"
        strokeWidth={stroke}
        strokeDasharray={`${arc} ${circ}`}
        strokeLinecap="round"
        style={{ transition: "stroke-dasharray 0.6s ease" }}
      />
    </svg>
  );
};

// ── OFENSIVA ─────────────────────────────────────────────────
export const OfensivaCard = ({ data, loading = false, onOpenVita, isActive = true, freezesRemaining = null }) => {
  // STREAK_DIAS já É a ofensiva correta: o servidor conta os dias ativos e trata
  // os dias congelados como PONTE (mantêm a sequência, mas NÃO somam ao número —
  // semântica Duolingo). Não inflar aqui (isso fazia 1 dia virar 2).
  const rawStreak = data?.STREAK_DIAS ?? 0;
  const streak = isStreakBroken(data?.LAST_ACTIVITY_DATE) ? 0 : rawStreak;
  // Recorde persistido no servidor (XP_VITA.MELHOR_STREAK). Fallback p/ o atual
  // só enquanto o backend ainda não gravou o campo.
  const bestStreak = Math.max(data?.MELHOR_STREAK ?? data?.BEST_STREAK ?? 0, streak);
  const animStreak = useTabCountUp(streak, isActive, 800);

  // Fogo aceso só se há streak ativo; apagado (com fumaça) se 0 ou quebrado.
  const fireLit = streak > 0 && data?.LAST_ACTIVITY_DATE === todayKeySP();

  // Linha de congelamentos — só p/ o próprio usuário (freezesRemaining != null)
  // e quando há freeze disponível ou há ofensiva ativa.
  const showFreezes = freezesRemaining != null && (freezesRemaining > 0 || streak > 0);

  if (loading) return (
    <BoxShadow className={Style.miniCard}>
      <Placeholder as="div" animation="glow" className={Style.miniCardSkeletonLabel}>
        <Placeholder xs={5} />
      </Placeholder>
      <div className={Style.ofensivaSkeletonBody}>
        <Placeholder animation="glow"><Placeholder xs={4} style={{ height: 48 }} /></Placeholder>
      </div>
    </BoxShadow>
  );

  return (
    <BoxShadow className={Style.miniCard}>

        <div className={Style.miniCardHeader}>
          <span className={Style.miniCardLabel}>OFENSIVA</span>
          <InfoTooltip text="Dias consecutivos ativos. Registre pelo menos 1 atividade por dia para manter a sequência e ganhar bônus de XP!" />
        </div>

        <button type="button" className={Style.miniCardBtn} onClick={onOpenVita}
          data-tutorial-id="xp-streak-circle">
          <div className={Style.ofensivaBody}>
            <div className={Style.ofensivaIconWrap}>
              <Fire variant="agited" lit={fireLit} size={0.85} />
            </div>
            <span className={Style.ofensivaCount}>{animStreak}</span>
            <span className={Style.ofensivaUnit}>dias</span>
            <span className={Style.ofensivaTag}>
              Sem falhar <FaFireAlt style={{ color: "#ff6b35", fontSize: 11 }} />
            </span>
          </div>

          <div className={Style.ofensivaMeta}>
            Melhor sequência: <strong>{bestStreak} dias</strong>
          </div>
          {showFreezes && (
            <div className={Style.ofensivaFreeze}>
              <PiSnowflakeFill size={16} className={Style.ofensivaFreezeIcon} />
              {/* "disponível(is)": o número agora soma a cota do mês + os
                  COMPRADOS (Pacote de Tickets Extra), que não expiram. */}
              {freezesRemaining} congelamento(s) disponível(is)
            </div>
          )}
        </button>
    </BoxShadow>
  );
};

// ── NÍVEL ─────────────────────────────────────────────────────
export const NivelCard = ({ data, loading = false, onOpenVita, isActive = true }) => {
  // Hooks ANTES do early return de `loading` — senão a contagem de hooks muda
  // quando o fetch termina e o React derruba o card ("Rendered more hooks
  // than during the previous render").
  const level          = data?.LEVEL ?? 1;
  const currentLevelXp = data?.CURRENT_LEVEL_XP ?? 0;
  const nextLevelXp    = data?.NEXT_LEVEL_XP ?? 100;
  const totalXp        = data?.TOTAL_XP ?? 0;
  const progress       = nextLevelXp > 0 ? Math.min(100, (currentLevelXp / nextLevelXp) * 100) : 0;

  const animLevel = useTabCountUp(level, isActive, 600);
  const animXp    = useTabCountUp(currentLevelXp, isActive, 1000);

  if (loading) return (
    <BoxShadow className={Style.miniCard}>
      <Placeholder as="div" animation="glow" className={Style.miniCardSkeletonLabel}><Placeholder xs={4} /></Placeholder>
      <div className={Style.nivelSkeletonArc}>
        <Placeholder animation="glow" style={{ width: 104, height: 104, borderRadius: "50%" }} />
      </div>
    </BoxShadow>
  );

  return (
    <BoxShadow className={Style.miniCard}>

      <div className={Style.miniCardHeader}>
        <span className={Style.miniCardLabel}>NÍVEL</span>
        <InfoTooltip text="Sobe conforme você acumula XP treinando, registrando refeições e fazendo avaliações físicas." />
      </div>

      <button type="button" className={Style.miniCardBtn} onClick={onOpenVita}
        data-tutorial-id="xp-level-circle">
        <div className={Style.nivelArcWrap}>
          <ArcProgress percent={progress} />
          <div className={Style.nivelArcCenter}>
            <span className={Style.nivelNumber}>{level}</span>
            <span className={Style.nivelLabel}>NÍVEL</span>
          </div>
        </div>

        <div className={Style.nivelXpRow}>
          <span className={Style.nivelXpCurrent}>{currentLevelXp}</span>
          <span className={Style.nivelXpSep}>/ {nextLevelXp} XP</span>
        </div>
        <div className={Style.nivelXpTotal}>
          <div className={Style.nivelXpIcon}>
            <PiStarFourFill width="22px" height="22px" color="var(--color-accent)" />
          </div>
          <span>{totalXp}</span> XP total</div>
      </button>
    </BoxShadow>
  );
};

export default NivelCard;
